import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Button, Grid, Link } from "@mui/material";
import { useHistory } from "react-router";
import theme from "../../theme";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ fontSize: 22, textAlign: "left" }}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 22, textAlign: "left" }}>
            {children}
          </Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function BasicTabs() {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue !== 2) {
      setValue(newValue);
    }
  };

  return (
    <Box>
      <Box>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          variant="scrollable"
          indicatorColor="secondary"
          TabIndicatorProps={{
            sx: { height: 4 },
          }}
        >
          <Tab
            label="Introduction"
            {...a11yProps(0)}
            sx={{
              [theme.breakpoints.up("xs")]: {
                fontSize: 15,
              },
              [theme.breakpoints.up("xl")]: {
                fontSize: 25,
              },
              fontWeight: 600,
              fontFamily: "Lora, serif",
            }}
          />
          <Tab
            label="About the Program"
            {...a11yProps(1)}
            sx={{
              [theme.breakpoints.up("xs")]: {
                fontSize: 15,
              },
              [theme.breakpoints.up("xl")]: {
                fontSize: 25,
                mx: "8em",
              },
              fontWeight: 600,
              fontFamily: "Lora, serif",
            }}
          />
          <Tab
            label="Who we are"
            {...a11yProps(2)}
            sx={{
              [theme.breakpoints.up("xs")]: {
                fontSize: 15,
              },
              [theme.breakpoints.up("xl")]: {
                fontSize: 25,
              },
              fontWeight: 600,
              fontFamily: "Lora, serif",
            }}
            rel="noopener noreferrer"
            href="https://www.claimscon.org/about/"
            target="_blank"
          />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <Typography
          sx={{
            [theme.breakpoints.up("xs")]: {
              fontSize: 14,
            },
            [theme.breakpoints.up("xl")]: {
              fontSize: 22,
            },
            textAlign: "left",
          }}
          component={"span"}
        >
          The Claims Conference helps more than 128,000 Holocaust survivors in
          over 41 countries through grantee partner agencies. We recognize there
          are Holocaust survivors living in countries that do not have access to
          these partner agencies. This program is intended to provide
          reimbursements for emergency expenses to eligible Holocaust survivors
          living outside of the over 41 countries funded by the Claims
          Conference. You can determine your eligibility and apply for the
          program entirely through this website. If you would prefer to complete
          a paper application form, please contact{" "}
          <Link href="mailto:eaprow@claimscon.org ">eaprow@claimscon.org</Link>{" "}
          and this form will be provided.
        </Typography>
        <br />
        <br />
        <Typography
          sx={{
            [theme.breakpoints.up("xs")]: {
              fontSize: 14,
            },
            [theme.breakpoints.up("xl")]: {
              fontSize: 22,
            },
            textAlign: "left",
          }}
          component={"span"}
        >
          You may be eligible for this program if you are a Holocaust survivor,
          live in a country without a Claims Conference funded program, and meet
          certain financial criteria. You must also have receipts for emergency
          expenses from this calendar year. Eligible expenses are medical care,
          dental care, food, heating and cooling bills, short-term homecare, and
          funeral expenses (reimbursement to be paid directly to the funeral
          home or the estate of the Jewish Nazi victim).
        </Typography>
        <br />
        <br />
        <Typography
          sx={{
            [theme.breakpoints.up("xs")]: {
              fontSize: 14,
            },
            [theme.breakpoints.up("xl")]: {
              fontSize: 22,
            },
            textAlign: "left",
          }}
          component={"span"}
        >
          EUR 1,000 is the cap for reimbursement for the current program year,
          but claimants may submit reimbursement requests exceeding this cap as
          it may change in the future. Payments will be made to living Holocaust
          survivors only. No payments will be made to spouses, second
          generation, or heirs.
        </Typography>
        <br />
        <br />

        <br />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <Typography
          sx={{
            [theme.breakpoints.up("xs")]: {
              fontSize: 14,
            },
            [theme.breakpoints.up("xl")]: {
              fontSize: 22,
            },
            textAlign: "left",
          }}
          component={"span"}
        >
          <b>Our Mission</b>
          <br />
          Our goal is to provide emergency funding to eligible Holocaust
          survivors who have been unable to access Claims Conference-funded
          social services because of where they live.
          <br />
          <br />
          <b>Eligibility Requirements</b>
          <br />
          Eligibility is based on the following:
          <br />-{" "}
          <Link href="https://www.claimscon.org/our-work/compensation/background/">
            You have previously received individual compensation as a Jewish
            victim of Nazi persecution. Second generation, spouses, and heirs
            are not eligible.
          </Link>
          <br />- You live in a country that does not receive Claims Conference
          funding. A summary of currently funded programs can be found{" "}
          <Link href="https://www.claimscon.org/wp-content/uploads/2022/06/2020-Worldbook.pdf">
            here.
          </Link>
          <br />
          - Your annual income is below the equivalent of EUR 22,500, including
          any pensions.
          <br />
          - Your assets, excluding a primary residence and automobile, are below
          the equivalent of EUR 22,500.
          <br />
          You will be asked to provide certain documentation to confirm you are
          eligible for this program.
          <br />
          <br />
          <b>Reimbursement Requests and Payments</b>
          <br />
          - Payments will be reimbursements for emergency expenses only.
          Eligible types of expenses are bills for medical care, dental care,
          food, heating and cooling, short-term homecare, and funeral expenses
          (reimbursement to be paid directly to the funeral home or the estate
          of the Jewish Nazi victim). Expenses must have already occurred and
          must be from the current calendar year. Receipt and proof of payment
          are required with each reimbursement request. EUR 1,000 is the cap for
          reimbursement for the current program year, but claimants may submit
          reimbursement requests exceeding this cap as it may change in the
          future. The actual amount paid by the Claims Conference may be less
          than the request depending on available funds.
          <br />
          <br />
          <b>The Application and Reimbursement Process</b>
          <br />
          The entire application process can be done through this website. There
          are a few steps to the process:
          <br />
          1) You will first be asked a series of questions to determine your
          eligibility. If your information meets the criteria, you will be
          invited to apply for the program.
          <br />
          2) You will apply on the portal. The application form will ask for
          certain documents and information so that we can verify your
          eligibility.
          <br />
          3) Once your application is submitted, we will review the
          information. If you are approved, you will receive credentials to
          create an account. You can track your existing reimbursement requests
          and submit new ones from your account.
        </Typography>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <Typography
          sx={{
            [theme.breakpoints.up("xs")]: {
              fontSize: 14,
            },
            [theme.breakpoints.up("xl")]: {
              fontSize: 22,
            },
            textAlign: "left",
          }}
        >
          70 Years of Pursuing Justice for Holocaust Survivors Worldwide
          <br />
          <br />
          The Conference on Jewish Material Claims Against Germany (Claims
          Conference), a nonprofit organization with offices in New York, Israel
          and Germany, secures material compensation for Holocaust survivors
          around the world.
          <br />
          <br />
          Founded in 1951 by representatives of 23 major international Jewish
          organizations, the Claims Conference negotiates for and disburses
          funds to individuals and organizations and seeks the return of Jewish
          property stolen during the Holocaust.
          <br />
          <br />
          As a result of negotiations with the Claims Conference since 1952, the
          German government has paid approximately $90 billion in
          indemnification to individuals for suffering and losses resulting from
          persecution by the Nazis.
          <br />
          <br />
          In 2023, the Claims Conference will distribute approximately $625
          million in direct compensation to over 260,000 survivors in 83
          countries and will allocate approximately $750 million in grants to
          over 300 social service agencies worldwide that provide vital services
          for Holocaust survivors, such as home care, food and medicine.
        </Typography>
      </CustomTabPanel>
    </Box>
  );
}
